import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { GridDataResult } from '@progress/kendo-angular-grid';
import {
  toDataSourceRequestString,
  translateDataSourceResultGroups,
} from '@progress/kendo-data-query';
import { ListViewDataResult } from '@progress/kendo-angular-listview';
import { map, Observable } from 'rxjs';
import { IChangeset } from '@shared-data';
import { environment } from 'src/environments/environment';
import { GridRowOverride } from '../models';
import { GridState } from '@shared';
import { ExportOption, ImportOptions } from '../constants';

@Injectable()
export class RestaurantItemService {
  apiUrl: string = environment.apiUrl + '/restaurantitem';
  constructor(private httpClient: HttpClient) {}

  getChangesets(type: string, state: GridState): Observable<GridDataResult> {
    const hasGroups = state.group && state.group.length;
    if (state.restaurantIds && state.restaurantIds.length > 0) {
      const restaurantIds = state.restaurantIds?.join(',');
      return this.httpClient
        .get<GridDataResult>(
          `${this.apiUrl}/${state.productType}/${type}?${toDataSourceRequestString(state)}&restaurantIds=${restaurantIds}`,
        )
        .pipe(
          map(({ data, total }) => ({
            data: hasGroups ? translateDataSourceResultGroups(data) : data,
            total: total,
          })),
        );
    }

    return this.httpClient
      .get<GridDataResult>(
        `${this.apiUrl}/${state.productType}/${type}?${toDataSourceRequestString(state)}&restaurantId=${state.restaurantId}`,
      )
      .pipe(
        map(({ data, total }) => ({
          data: hasGroups ? translateDataSourceResultGroups(data) : data,
          total: total,
        })),
      );
  }

  saveChangesets(
    type: string,
    changesets: IChangeset[],
    productType: string,
    applyToRestaurants?: string[],
  ): Observable<any> {
    const url = applyToRestaurants
      ? `${this.apiUrl}/${productType}/${type}/bulk?applyToRestaurants=${applyToRestaurants.join(',')}`
      : `${this.apiUrl}/${productType}/${type}/bulk`;
    return this.httpClient.post<any>(url, changesets);
  }

  exportToExcel(
    type: string,
    restaurantId: string,
    productType: string,
    exportOption: ExportOption,
  ): Observable<Blob> {
    switch (exportOption) {
      case 'Current Type':
      default:
        return this.httpClient.get(
          `${this.apiUrl}/${productType}/${type}/export?restaurantId=${restaurantId}`,
          {
            responseType: 'blob',
          },
        );
      case 'Current Type (All Tiers)':
        return this.httpClient.get(
          `${this.apiUrl}/${productType}/pricing/exportalltiers?restaurantId=${restaurantId}`,
          {
            responseType: 'blob',
          },
        );
      case 'All':
        return this.httpClient.get(`${this.apiUrl}/all/pricing/exportbytype`, {
          responseType: 'blob',
        });
    }
  }

  getDistinctItems(
    state: GridState,
    type: string,
    field: string,
    restaurantId?: string,
  ): Observable<ListViewDataResult> {
    const url = restaurantId
      ? `${this.apiUrl}/${state.productType}/${type}/distinctItems/${field}?${toDataSourceRequestString(state)}&restaurantId=${restaurantId}`
      : `${this.apiUrl}/${state.productType}/${type}/distinctItems/${field}?${toDataSourceRequestString(state)}`;
    return this.httpClient.get<ListViewDataResult>(url);
  }

  getImportUrls(productType: string, type: string): Map<ImportOptions, string> {
    return new Map<ImportOptions, string>([
      ['Current Type', `${this.apiUrl}/${productType}/${type}/import`],
      ['All', `${this.apiUrl}/all/pricing/importbytype`],
    ]);
  }

  getOverrides(
    state: GridState,
    componentId: number,
    onlyEntrees: boolean,
  ): any {
    return this.httpClient.get<GridDataResult>(
      `${this.apiUrl}/overrides?${toDataSourceRequestString(state)}&restaurantId=${state.restaurantId}&componentId=${componentId}&onlyEntrees=${onlyEntrees}`,
    );
  }

  saveOverrides(changesets: GridRowOverride[]): Observable<IChangeset[]> {
    const url = `${this.apiUrl}/overrides/bulk`;
    return this.httpClient.post<IChangeset[]>(url, changesets);
  }

  loadRbiRecommendations(restaurantId: string, productType: string): Observable<void> {
    return this.httpClient.post<void>(`${this.apiUrl}/${productType}/pricing/loadRecommendations?restaurantId=${restaurantId}`, {});
  }
}
